import {
    Badge,
    Box,
    Button,
    HStack,
    Icon,
    IconButton,
    Spinner,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    VStack,
} from '@chakra-ui/react';
import axios from 'axios';
import { useContext, useMemo, useState } from 'react';
import { TbChevronLeft, TbChevronRight, TbExternalLink } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';
import API_URL from '../../constants/environment';
import SessionContext from '../../contexts/SessionContext';
import { isEmbedMode } from '../../utils/embedMode';

/**
 * Interactive results table injected into Ask Carl responses by the
 * query_orders tool (multi-dimensional order search: supplier, PO, part
 * number, location, vehicle, status filter) and by list_orders (RO Manager /
 * Dashboard feeds). Each row links to the order detail page so the user can
 * act on the match. Paginates in-card and can load further pages from the
 * original query without another Carl turn.
 */

export interface OrdersTableRowSnapshot {
    orderId?: string;
    roNumber?: string;
    vehicle?: string;
    customer?: string | null;
    location?: string | null;
    daysActive?: string;
    archived?: boolean;
    completed?: boolean;
    parts?: {
        total?: number;
        newParts?: number;
        quoting?: number;
        pendingOrder?: number;
        ordered?: number;
    };
    matchedSupplierOrders?: Array<{
        supplierText?: string;
        supplier?: string;
        displayId?: string | null;
        po?: string | null;
        status?: string;
    }>;
    matchedParts?: Array<{
        title?: string;
        partNumber?: string;
        status?: string;
    }>;
}

/** Enough of the original tool call for the card to fetch subsequent pages. */
export type OrdersTableQuery =
    | {
        source: 'ro-manager';
        filter: string;
        search?: string;
        limit: number;
        offset: number;
        additionalFilters?: { daysActiveSort?: 'asc' | 'desc' };
    }
    | {
        source: 'dashboard';
        filter: string;
        search?: string;
        limit: number;
        offset: number;
        additionalFilters?: { daysActiveSort?: 'asc' | 'desc' };
    }
    | {
        source: 'query';
        supplierName?: string;
        po?: string;
        partNumber?: string;
        locationName?: string;
        vehicleName?: string;
        search?: string;
        statusFilter?: string;
        supplierOrderStatus?: string;
        createdAfter?: string;
        createdBefore?: string;
        /** Absent means the server's own default: relevance for free text, else newest. */
        sortBy?: string;
        excludeTotalsAndQuotes?: boolean;
        includeInactive?: boolean;
        limit?: number;
        offset?: number;
    };

export interface OrdersTableSnapshot {
    orders: Array<OrdersTableRowSnapshot>;
    hasMore?: boolean;
    query?: OrdersTableQuery;
}

const PAGE_SIZE = 10;

/**
 * Content identity of a snapshot. The assistant message that owns this card
 * keeps a stable id while its `ui` payload is replaced (a second order search
 * in the same turn reuses this component instance), so paging state is keyed on
 * what the snapshot CONTAINS rather than on the object reference.
 */
const snapshotIdentity = (table: OrdersTableSnapshot): string => JSON.stringify({
    query: table.query ?? null,
    rows: (Array.isArray(table.orders) ? table.orders : [])
        .map((row) => row.orderId ?? row.roNumber ?? ''),
});

interface PagingState {
    /** Snapshot this state was seeded from; stale async results are discarded. */
    identity: string;
    rows: Array<OrdersTableRowSnapshot>;
    hasMore: boolean;
    /** Offset of the next page to request from the originating query. */
    nextOffset: number;
    page: number;
    loadingMore: boolean;
    loadError: string | null;
}

const seedPaging = (table: OrdersTableSnapshot, identity: string): PagingState => {
    const rows = Array.isArray(table.orders) ? table.orders : [];
    return {
        identity,
        rows,
        hasMore: Boolean(table.hasMore),
        nextOffset: (table.query?.offset ?? 0) + (table.query?.limit ?? rows.length),
        page: 0,
        loadingMore: false,
        loadError: null,
    };
};

/** "2 new · 3 unordered" — the actionable pending-parts summary for a row. */
const pendingSummary = (parts?: OrdersTableRowSnapshot['parts']): string => {
    if (!parts) return '—';
    // Feed rows without a breakdown still show "—" rather than a misleading "None".
    const hasBreakdown = [parts.newParts, parts.quoting, parts.pendingOrder]
        .some((value) => typeof value === 'number');
    if (!hasBreakdown) return '—';
    const segments: string[] = [];
    if (parts.newParts) segments.push(`${parts.newParts} new`);
    if (parts.quoting) segments.push(`${parts.quoting} quoting`);
    if (parts.pendingOrder) segments.push(`${parts.pendingOrder} unordered`);
    return segments.length ? segments.join(' · ') : 'None';
};

const asString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.length > 0 ? value : undefined;
const asNumber = (value: unknown): number | undefined =>
    typeof value === 'number' ? value : undefined;

/** Normalize a raw feed / queryOrders row into the table snapshot shape. */
const normalizeRow = (order: Record<string, unknown>): OrdersTableRowSnapshot => {
    const firstName = asString(order.firstName);
    const lastName = asString(order.lastName);
    const nestedParts = order.parts && typeof order.parts === 'object'
        ? order.parts as OrdersTableRowSnapshot['parts']
        : undefined;
    return {
        orderId: asString(order.orderId) ?? (order._id ? String(order._id) : undefined),
        roNumber: asString(order.roNumber),
        vehicle: asString(order.ymm) ?? asString(order.vehicle),
        customer: asString(order.customer)
            ?? (lastName || firstName ? [lastName, firstName].filter(Boolean).join(', ') : undefined),
        location: asString(order.locationName) ?? asString(order.location),
        daysActive: asString(order.daysOpen) ?? asString(order.daysActive),
        archived: order.archived === true || undefined,
        completed: order.completed === true || undefined,
        parts: nestedParts ?? {
            total: asNumber(order.totalParts) ?? asNumber(order.partsTotal),
            ordered: asNumber(order.partsOrdered),
            newParts: asNumber(order.newParts),
            quoting: asNumber(order.quoting),
            pendingOrder: asNumber(order.pendingOrder),
        },
        matchedSupplierOrders: order.matchedSupplierOrders as OrdersTableRowSnapshot['matchedSupplierOrders'],
        matchedParts: order.matchedParts as OrdersTableRowSnapshot['matchedParts'],
    };
};

const OrdersTableCard = ({ table }: { table: OrdersTableSnapshot }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const sessionContext = useContext(SessionContext);
    // Mobile WebView embed: the chat is the whole app surface — no jumping
    // out to the order detail page (App would bounce it back anyway).
    const isEmbedded = isEmbedMode(location.search);

    const identity = useMemo(() => snapshotIdentity(table), [table]);
    const [paging, setPaging] = useState<PagingState>(() => seedPaging(table, identity));

    // A new search can land on this same component instance, so re-seed during
    // render — an effect would first paint the previous result set.
    const current = paging.identity === identity ? paging : seedPaging(table, identity);
    if (current !== paging) setPaging(current);
    const { rows, hasMore, nextOffset, page, loadingMore, loadError } = current;

    const setPage = (next: number) => setPaging((prev) => ({ ...prev, page: next }));

    const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount - 1);
    const pageRows = useMemo(
        () => rows.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE),
        [rows, safePage],
    );

    if (!rows.length) return null;

    const hasSupplierContext = rows.some((row) => row.matchedSupplierOrders?.length);
    const hasPartContext = rows.some((row) => row.matchedParts?.length);
    const canGoPrev = safePage > 0;
    const canGoNextInLoaded = safePage < pageCount - 1;
    const canLoadMore = hasMore && Boolean(table.query) && Boolean(sessionContext?.sessionId);

    const openOrder = (orderId?: string) => {
        if (!orderId || isEmbedded) return;
        navigate(`/order/${orderId}`);
    };

    const loadMore = async () => {
        if (!table.query || !sessionContext?.sessionId || loadingMore) return;
        // Captured so a response that arrives after a new search is discarded.
        const requestIdentity = identity;
        setPaging((prev) => (prev.identity !== requestIdentity
            ? prev
            : { ...prev, loadingMore: true, loadError: null }));
        try {
            const limit = table.query.limit ?? 20;
            let fetched: Array<Record<string, unknown>> = [];
            let more = false;

            if (table.query.source === 'ro-manager') {
                const { data } = await axios.post(
                    `${API_URL}/order/home/feed`,
                    {
                        limit,
                        offset: nextOffset,
                        locationIds: 'all',
                        filters: [table.query.filter],
                        search: table.query.search,
                        additionalFilters: table.query.additionalFilters
                            ?? { daysActiveSort: 'desc' },
                    },
                    { headers: { Authorization: sessionContext.sessionId } },
                );
                fetched = (data.orders ?? []) as Array<Record<string, unknown>>;
                more = fetched.length >= limit;
            } else if (table.query.source === 'dashboard') {
                const { data } = await axios.post(
                    `${API_URL}/orders/feed`,
                    {
                        type: table.query.filter,
                        limit,
                        offset: nextOffset,
                        locationIds: 'all',
                        search: table.query.search,
                        additionalFilters: table.query.additionalFilters
                            ?? { daysActiveSort: 'desc' },
                    },
                    { headers: { Authorization: sessionContext.sessionId } },
                );
                fetched = (data.orders ?? []) as Array<Record<string, unknown>>;
                more = fetched.length >= limit;
            } else {
                const { source: _source, ...queryBody } = table.query;
                const { data } = await axios.post(
                    `${API_URL}/orders/query`,
                    {
                        ...queryBody,
                        locationIds: 'all',
                        limit,
                        offset: nextOffset,
                    },
                    { headers: { Authorization: sessionContext.sessionId } },
                );
                fetched = (data.orders ?? []) as Array<Record<string, unknown>>;
                more = Boolean(data.hasMore);
            }

            const normalized = fetched.map(normalizeRow);
            setPaging((prev) => {
                if (prev.identity !== requestIdentity) return prev;
                const seen = new Set(prev.rows.map((row) => row.orderId).filter(Boolean));
                const appended = normalized.filter((row) => !row.orderId || !seen.has(row.orderId));
                return {
                    ...prev,
                    rows: [...prev.rows, ...appended],
                    hasMore: more,
                    nextOffset: prev.nextOffset + limit,
                    // Jump to the first page that includes newly loaded rows.
                    page: appended.length > 0 ? Math.floor(prev.rows.length / PAGE_SIZE) : prev.page,
                    loadingMore: false,
                };
            });
        } catch {
            setPaging((prev) => (prev.identity !== requestIdentity
                ? prev
                : { ...prev, loadingMore: false, loadError: 'Could not load more orders. Please try again.' }));
        }
    };

    const goNext = () => {
        if (canGoNextInLoaded) {
            setPage(safePage + 1);
            return;
        }
        if (canLoadMore) void loadMore();
    };

    return (
        <Box border="1px solid" borderColor="gray.200" borderRadius="xl" bg="white" boxShadow="none" px={4} py={3.5}>
            <VStack align="stretch" spacing={3}>
                <HStack justify="space-between" align="center">
                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        Matching Orders
                    </Text>
                    <HStack spacing={2}>
                        <Badge colorScheme="blue" variant="subtle">
                            {rows.length}{hasMore ? '+' : ''} {rows.length === 1 && !hasMore ? 'order' : 'orders'}
                        </Badge>
                    </HStack>
                </HStack>

                <TableContainer border="1px solid" borderColor="gray.200" borderRadius="lg" bg="gray.50">
                    <Table size="sm" variant="simple">
                        <Thead>
                            <Tr>
                                <Th fontSize="10px" color="gray.500" borderColor="gray.200">RO</Th>
                                <Th fontSize="10px" color="gray.500" borderColor="gray.200">Vehicle</Th>
                                <Th fontSize="10px" color="gray.500" borderColor="gray.200">Customer</Th>
                                <Th fontSize="10px" color="gray.500" borderColor="gray.200">Location</Th>
                                <Th fontSize="10px" color="gray.500" borderColor="gray.200">Pending Parts</Th>
                                <Th fontSize="10px" color="gray.500" borderColor="gray.200" isNumeric>Ordered</Th>
                                {hasSupplierContext && (
                                    <Th fontSize="10px" color="gray.500" borderColor="gray.200">Supplier / PO</Th>
                                )}
                                {hasPartContext && (
                                    <Th fontSize="10px" color="gray.500" borderColor="gray.200">Matched Part</Th>
                                )}
                                <Th fontSize="10px" color="gray.500" borderColor="gray.200">Age</Th>
                                {!isEmbedded && <Th borderColor="gray.200" w="32px" />}
                            </Tr>
                        </Thead>
                        <Tbody>
                            {pageRows.map((row, index) => (
                                <Tr
                                    key={row.orderId ?? `${row.roNumber}-${safePage}-${index}`}
                                    cursor={row.orderId && !isEmbedded ? 'pointer' : undefined}
                                    _hover={row.orderId && !isEmbedded ? { bg: 'blue.50' } : undefined}
                                    onClick={() => openOrder(row.orderId)}
                                >
                                    <Td fontSize="xs" borderColor="gray.200">
                                        <HStack spacing={1.5}>
                                            <Text fontWeight="bold" color="#0071E3">{row.roNumber || '—'}</Text>
                                            {row.archived && <Badge colorScheme="gray" variant="subtle" fontSize="9px">Archived</Badge>}
                                            {row.completed && <Badge colorScheme="green" variant="subtle" fontSize="9px">Completed</Badge>}
                                        </HStack>
                                    </Td>
                                    <Td fontSize="xs" color="gray.700" borderColor="gray.200">{row.vehicle || '—'}</Td>
                                    <Td fontSize="xs" color="gray.700" borderColor="gray.200">{row.customer || '—'}</Td>
                                    <Td fontSize="xs" color="gray.700" borderColor="gray.200">{row.location || '—'}</Td>
                                    <Td fontSize="xs" color="gray.700" borderColor="gray.200">{pendingSummary(row.parts)}</Td>
                                    <Td fontSize="xs" color="gray.700" borderColor="gray.200" isNumeric>
                                        {typeof row.parts?.ordered === 'number' && typeof row.parts?.total === 'number'
                                            ? `${row.parts.ordered} / ${row.parts.total}`
                                            : '—'}
                                    </Td>
                                    {hasSupplierContext && (
                                        <Td fontSize="xs" color="gray.700" borderColor="gray.200">
                                            <VStack align="flex-start" spacing={0.5}>
                                                {(row.matchedSupplierOrders ?? []).slice(0, 3).map((supplierOrder, supplierIndex) => {
                                                    const name = supplierOrder.supplierText ?? supplierOrder.supplier ?? '—';
                                                    const po = supplierOrder.displayId ?? supplierOrder.po;
                                                    return (
                                                        <Text key={`${name}-${supplierIndex}`} noOfLines={1}>
                                                            {name}
                                                            {po ? (
                                                                <Text as="span" color="gray.500" fontFamily="mono"> · {po}</Text>
                                                            ) : null}
                                                        </Text>
                                                    );
                                                })}
                                            </VStack>
                                        </Td>
                                    )}
                                    {hasPartContext && (
                                        <Td fontSize="xs" color="gray.700" borderColor="gray.200">
                                            <VStack align="flex-start" spacing={0.5}>
                                                {(row.matchedParts ?? []).slice(0, 3).map((part, partIndex) => (
                                                    <Text key={`${part.partNumber}-${partIndex}`} noOfLines={1}>
                                                        {part.title || '—'}
                                                        {part.partNumber ? (
                                                            <Text as="span" color="gray.500" fontFamily="mono"> · {part.partNumber}</Text>
                                                        ) : null}
                                                    </Text>
                                                ))}
                                            </VStack>
                                        </Td>
                                    )}
                                    <Td fontSize="xs" color="gray.700" borderColor="gray.200" whiteSpace="nowrap">{row.daysActive || '—'}</Td>
                                    {!isEmbedded && (
                                        <Td borderColor="gray.200">
                                            {row.orderId && (
                                                <Tooltip label="Open order" placement="top">
                                                    <IconButton
                                                        aria-label={`Open order ${row.roNumber ?? ''}`}
                                                        icon={<Icon as={TbExternalLink as React.ElementType} w={3.5} h={3.5} />}
                                                        size="xs"
                                                        variant="ghost"
                                                        color="#0071E3"
                                                        _hover={{ bg: 'blue.100' }}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            openOrder(row.orderId);
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </Td>
                                    )}
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </TableContainer>

                <HStack justify="space-between" align="center">
                    <Text fontSize="xs" color="gray.500">
                        Page {safePage + 1} of {pageCount}{hasMore ? '+' : ''}
                        {/* Replayed snapshots arrive without the originating query, so
                            more matches exist but can't be fetched from the card. */}
                        {hasMore ? (canLoadMore ? ' · more available' : ' · more matches not shown') : ''}
                    </Text>
                    <HStack spacing={2}>
                        <Button
                            size="xs"
                            variant="outline"
                            leftIcon={<Icon as={TbChevronLeft as React.ElementType} />}
                            onClick={() => setPage(safePage - 1)}
                            isDisabled={!canGoPrev || loadingMore}
                        >
                            Prev
                        </Button>
                        <Button
                            size="xs"
                            variant="outline"
                            rightIcon={loadingMore
                                ? <Spinner size="xs" />
                                : <Icon as={TbChevronRight as React.ElementType} />}
                            onClick={goNext}
                            isDisabled={(!canGoNextInLoaded && !canLoadMore) || loadingMore}
                        >
                            {canGoNextInLoaded || !canLoadMore ? 'Next' : 'Load more'}
                        </Button>
                    </HStack>
                </HStack>

                {loadError && (
                    <Text fontSize="xs" color="red.500">{loadError}</Text>
                )}
            </VStack>
        </Box>
    );
};

export default OrdersTableCard;
